/** Nommage des rangements créés en série / grille (partagé API + web). */

export const LOCATION_BATCH_MAX = 100
/** Au-delà, l'UI demande une confirmation explicite. */
export const LOCATION_BATCH_CONFIRM_THRESHOLD = 50

export type GridCellRef = { id: string; gridRow: number; gridCol: number; name: string }

/**
 * Si tous les rangements ont `grid_row` / `grid_col`, renvoie la disposition
 * (lignes × colonnes, cases indexées). Sinon `null` (affichage liste / galerie).
 */
export function detectLocationGrid(
  locations: ReadonlyArray<{ id: string; name: string; gridRow: number | null; gridCol: number | null }>,
): { rows: number; cols: number; byCoord: Map<string, GridCellRef> } | null {
  if (locations.length === 0) return null
  if (!locations.every((l) => l.gridRow != null && l.gridCol != null && l.gridRow >= 0 && l.gridCol >= 0)) {
    return null
  }
  const rows = Math.max(...locations.map((l) => l.gridRow!)) + 1
  const cols = Math.max(...locations.map((l) => l.gridCol!)) + 1
  if (rows * cols > LOCATION_BATCH_MAX * 2) return null
  const byCoord = new Map<string, GridCellRef>()
  for (const l of locations) {
    byCoord.set(`${l.gridRow},${l.gridCol}`, {
      id: l.id,
      name: l.name,
      gridRow: l.gridRow!,
      gridCol: l.gridCol!,
    })
  }
  return { rows, cols, byCoord }
}

/** 0 → A, 25 → Z, 26 → AA. */
export function indexToLetters(index: number): string {
  if (index < 0 || !Number.isInteger(index)) throw new RangeError('index')
  let i = index
  let out = ''
  do {
    out = String.fromCharCode(65 + (i % 26)) + out
    i = Math.floor(i / 26) - 1
  } while (i >= 0)
  return out
}

export type GridNamePlan = {
  mode: 'grid'
  rows: number
  cols: number
  prefix?: string
}

export type SeriesNamePlan = {
  mode: 'series'
  prefix: string
  count: number
  /** Début de numérotation (défaut 1 pour les chiffres, 0 = A pour les lettres). */
  start?: number
  naming: 'number' | 'letter'
}

export type BatchNamePlan = GridNamePlan | SeriesNamePlan

export type PlannedLocationName = {
  name: string
  gridRow: number | null
  gridCol: number | null
  position: number
}

function withPrefix(prefix: string | undefined, label: string): string {
  const p = prefix?.trim()
  return p ? `${p} ${label}` : label
}

/** Aperçu des noms qui seront créés (même logique que l'API). */
export function planLocationBatchNames(plan: BatchNamePlan): PlannedLocationName[] {
  if (plan.mode === 'grid') {
    const total = plan.rows * plan.cols
    if (total < 1 || total > LOCATION_BATCH_MAX) throw new RangeError('batch_size')
    const out: PlannedLocationName[] = []
    let position = 0
    for (let r = 0; r < plan.rows; r++) {
      for (let c = 0; c < plan.cols; c++) {
        const label = `${indexToLetters(r)}${c + 1}`
        out.push({
          name: withPrefix(plan.prefix, label),
          gridRow: r,
          gridCol: c,
          position: position++,
        })
      }
    }
    return out
  }

  if (plan.count < 1 || plan.count > LOCATION_BATCH_MAX) throw new RangeError('batch_size')
  const start = plan.start ?? (plan.naming === 'letter' ? 0 : 1)
  const out: PlannedLocationName[] = []
  for (let i = 0; i < plan.count; i++) {
    const n = start + i
    const label = plan.naming === 'letter' ? indexToLetters(n) : String(n)
    const name = plan.prefix.trim() ? `${plan.prefix.trim()} ${label}` : label
    out.push({ name, gridRow: null, gridCol: null, position: i })
  }
  return out
}
